import { onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * Bidirectional sync with the native `<select>`: mirrors its options /
 * selection / multiple / disabled state into reactive refs, observes
 * external mutations, and provides a `pick(option)` that updates the
 * native element and emits input/change events for form integration.
 *
 * @param {HTMLSelectElement} nativeSelect
 */
export function useNativeSelect(nativeSelect) {
    const options = ref([]);
    const selectedValues = ref([]);
    const multiple = ref(false);
    const disabled = ref(false);

    function readNative() {
        multiple.value = nativeSelect.multiple;
        disabled.value = nativeSelect.disabled;
        options.value = Array.from(nativeSelect.options).map((opt) => ({
            value: opt.value,
            label: (opt.label || opt.textContent || '').trim() || opt.value,
            disabled: opt.disabled,
        }));
        selectedValues.value = Array.from(nativeSelect.selectedOptions).map(
            (o) => o.value,
        );
    }

    /**
     * Selects (or toggles, in multi-mode) an option on the native element
     * and fires the input/change events so any form listeners react.
     */
    function pick(opt) {
        if (opt.disabled) return;
        if (multiple.value) {
            const native = Array.from(nativeSelect.options).find(
                (o) => o.value === opt.value,
            );
            if (!native) return;
            native.selected = !native.selected;
        } else {
            nativeSelect.value = opt.value;
        }
        nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        readNative();
    }

    /**
     * Clears any selection. Useful when an explicit placeholder is configured
     * but no option has the HTML `selected` attribute — see hasExplicitSelection().
     */
    function deselect() {
        nativeSelect.selectedIndex = -1;
        readNative();
    }

    /**
     * True if at least one `<option>` carries the HTML `selected` attribute,
     * meaning the page author has an explicit initial choice. When false,
     * the browser has auto-picked the first option.
     */
    function hasExplicitSelection() {
        return Array.from(nativeSelect.options).some((o) => o.defaultSelected);
    }

    let mutationObserver = null;
    function onNativeChange() {
        readNative();
    }

    onMounted(() => {
        readNative();
        nativeSelect.addEventListener('change', onNativeChange);
        mutationObserver = new MutationObserver(() => readNative());
        mutationObserver.observe(nativeSelect, {
            attributes: true,
            attributeFilter: ['disabled', 'multiple'],
            childList: true,
            subtree: true,
            characterData: true,
        });
    });

    onBeforeUnmount(() => {
        nativeSelect.removeEventListener('change', onNativeChange);
        mutationObserver?.disconnect();
    });

    return {
        options,
        selectedValues,
        multiple,
        disabled,
        pick,
        deselect,
        hasExplicitSelection,
    };
}
